import { useForm } from 'react-hook-form';
import Link from 'next/link';
import { useRouter } from 'next/router';

import MarketingLayout from '../../components/marketing-site/MarketingLayout';
import { MethodData, useAxios } from '../../lib/custom-axios/CustomAxios';
import { getInTouchPath, initPath } from '../../lib/api/Api';
import { browseMarketplaceByLocation } from '../../components/marketing-site/marketing-utils';
import { CONTACT_REASONS } from '../../lib/interfaces/contact-form/reasons';
import InputLabelWithValidation from '../../lib/validations/InputLabelWithValidation';
import { validateEmailAddress, validateInputIsNotEmpty } from '../../lib/validations/validations';
import { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../contexts/user/UserContext';
import TextAreaFormControl from '../../lib/input/text-area-form-control/TextAreaFormControl';
import type { GetServerSideProps } from "next";
import absoluteUrl from 'next-absolute-url';
import axios from 'axios';
import { init } from 'i18next';
import { NextSeo } from 'next-seo';

export interface IContactProps {
    initData: any;
}

export const getServerSideProps = (async ({req}) => {
    const {origin} = absoluteUrl(req);

    const initDataResponse = await axios({
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8'
        },
        url: `${origin}${initPath}`
    });

    return {
        props: {
           initData: initDataResponse.data
        }
    }
}) satisfies GetServerSideProps<IContactProps>

const Contact: React.FC = () => {
  const router = useRouter();
  const {data, loading, error, fetch} = useAxios();
  const query = router.query as { type: CONTACT_REASONS };

  const {user} = useContext(UserContext)

  const form = useForm({
    shouldUseNativeValidation: false,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: undefined,
    context: undefined,
    criteriaMode: 'all',
    shouldFocusError: true,
    shouldUnregister: true,
    defaultValues: {
      interestedIn: query?.type === CONTACT_REASONS.MISSING_VEHICLE_OPTION ? 'selling' : '',
      fullName: user ? `${user.firstName} ${user.lastName}` : null,
      email: user?.email || null,
      message: null
    }
  });

  useEffect(() => {
    if (user) {
      form.setValue('email', user?.email || null)
      form.setValue('fullName', user ? `${user.firstName} ${user.lastName}` : null)
    }
  }, [user]);

  const onFormSubmitHandeler = (data: any) => {
    fetch(getInTouchPath, {
      method: MethodData.POST,
      body: data
    });
  }
  return (
    <MarketingLayout>
      {!data ? (
        <>
          <NextSeo
            title="Get in Touch with Purr: Sell Your Car Effortlessly"
            description="Effortlessly sell your car for more with Purr. Contact us for support or browse our inventory now!"
          />
          <section id="contactTitle" className="bg-gray masthead contact">
            <div className="container custom-container">
              <div className="row">
                <div className="col-12 col-lg-5">
                  <h3 className="mb-3">Have more questions?</h3>
                  <h1>Get in touch with us.</h1>
                </div>
                <div className="d-flex justify-content-end banner-image col-12 col-lg-6 offset-md-1">
                  <img src="/marketing-assets/images/contact-hero.png"/>
                </div>
              </div>
            </div>
          </section>
          <section id='form' className="bg-gray form">
            <div className="container custom-container">
              <div className="row">
                <div className="col-12 col-lg-6">
                  <h2>
                    {/* <a href="tel:1-888-998-7877">1-888-998-PURR</a>
                    <br/> */}
                    <a href="mailto:support@purr.ca">support@purr.ca</a>
                  </h2>
                  <div className="mt-5">
                    <p className="m-0">Mon-Fri: 8:00 a.m. – 8:00 p.m. ET</p>
                    <p className="m-0">Sat: 9:00 a.m. – 7:00 p.m. ET</p>
                  </div>
                  <div className="mt-5">
                    <p className="mb-2"><strong>Purr proudly serves</strong></p>
                    <Link legacyBehavior href={browseMarketplaceByLocation('51.253775', '-87.323214', 'administrative_area_level_1')}>
                      <a className='text-decoration-none'><strong>Ontario</strong></a>
                    </Link>
                    <ul className="d-flex mb-3">
                      <div className="col-6">
                        <li>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('43.653226', '-79.3831843', 'locality')}>
                            Toronto
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('43.464258', '-80.52041', 'locality')}>
                            Waterloo
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('43.361621', '-80.314428', 'locality')}>
                            Cambridge
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('43.45164', '-80.492534', 'locality')}>
                            Kitchener
                          </Link>
                        </li>
                      </div>
                      <div className="col-6">
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('43.139387', '-80.264425', 'locality')}>
                            Brantford
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('43.544805', '-80.248167', 'locality')}>
                            Guelph
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('43.370001', '-80.982229', 'locality')}>
                            Stratford
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('51.253775', '-87.323214', 'administrative_area_level_1')}>
                            Surrounding Areas
                          </Link>
                        </li>
                      </div>
                    </ul>
                    <ul className='d-flex mb-0 mb-md-4'>
                      <div className='col-6'>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('54.4216878', '-119.4841678', 'administrative_area_level_1')}>
                            <a><strong>Central Alberta</strong></a>
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('52.3065443', '-114.0973313', 'locality')}>
                            Sylvan Lake
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('52.268975', '-113.8115599', 'locality')}>
                            Red Deer
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('52.38087909999999', '-113.7954188', 'locality')}>
                            Blackfalds
                          </Link>
                        </li>
                      </div>
                      <div className='col-6 align-self-end'>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('52.4684651', '-113.7306644', 'locality')}>
                            Lacombe
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('52.136699', '-113.8723875', 'locality')}>
                            Penhold
                          </Link>
                        </li>
                        <li>
                          <Link legacyBehavior href={browseMarketplaceByLocation('52.02739219999999', '-113.9501957', 'locality')}>
                            Innisfail
                          </Link>
                        </li>
                      </div>
                    </ul>
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <form id="contact-form" onSubmit={form.handleSubmit(onFormSubmitHandeler)}>
                    <div id="form-container">
                      {error && (
                        <div id="error-submit" className="alert alert-danger show" role="alert">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M1.51072 17.0475L9.08572 3.9375C10.3907 1.6875 13.6307 1.6875 14.9357 3.9375L22.4957 17.0475C23.7857 19.2975 22.1657 22.1175 19.5707 22.1175H4.43572C1.84072 22.1175 0.205716 19.2975 1.51072 17.0475ZM11.9551 14.295C11.3851 14.265 10.9051 13.8 10.8901 13.23L10.6351 8.91C10.6351 8.55 10.7551 8.205 10.9951 7.935C11.2351 7.665 11.5801 7.515 11.9401 7.5C12.7351 7.5 13.3351 8.07 13.3651 8.805L13.1251 13.23C13.0951 13.83 12.6151 14.295 12.0151 14.295H11.9551ZM12.0001 18.7049C11.1151 18.7049 10.3951 17.9849 10.3951 17.0999C10.3951 16.2149 11.1151 15.4949 12.0001 15.4949C12.8851 15.4949 13.6051 16.2149 13.6051 17.0999C13.6051 17.9999 12.8851 18.7049 12.0001 18.7049Z" fill="#222222"/>
                          </svg>
                          <span>There was an error sending your request, please try again later</span>
                        </div>
                      )}
                      <div className="mb-3">
                        <InputLabelWithValidation
                          label='Full name'
                          error={form.formState.errors.fullName}
                        />
                        <input
                          type="text"
                          className="form-control"
                          {...form.register('fullName', {
                            required: true,
                            maxLength: 100,
                            validate: {
                              nonEmpty: (value: string | null) => validateInputIsNotEmpty(value || '')
                            }
                          })}
                        />
                      </div>
                      <div className="mb-3">
                        <InputLabelWithValidation
                          label='I’m interested in'
                          error={form.formState.errors.interestedIn}
                        />
                        <select
                          className="form-select"
                          required {...form.register('interestedIn', {required: true})}>
                          <option value=""></option>
                          <option value="buying">Buying</option>
                          <option value="selling">Selling</option>
                          <option value="dealership">Dealership</option>
                        </select>
                      </div>
                      <div className="mb-3">
                        <InputLabelWithValidation
                          label='Email'
                          error={form.formState.errors.email}
                        />
                        <input
                          type="email"
                          className="form-control"
                          {...form.register('email', {
                            required: true,
                            validate: {
                              invalidEmail: (value: string | null) => validateEmailAddress(value || '')
                            }
                          })}
                        />
                      </div>
                      <div className="mb-3 numNoArrows">
                        <InputLabelWithValidation
                          label='Message'
                          error={form.formState.errors.message}
                        />
                        <TextAreaFormControl
                          maxCharsCount={1000}
                          error={form?.formState?.errors?.message}
                          inputProps={{...form.register('message', {
                            required: true,
                            maxLength: 1000
                          })}}
                        />
                      </div>
                      <div className="d-flex align-items-center justify-content-center mt-4">
                        <button type="submit" id="submit" className={`btn btn-purr btn-lg ${loading ? 'loading' : ''}`} disabled={loading}>
                          Send message
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <section id="contactSuccess" className="bg-gray masthead contact-success">
          <div className="container custom-container d-flex justify-content-center">
            <div className="w-50">
                <h3 className="mb-3 text-center">Thanks!</h3>
                <h1 className="mb-3 text-center">We’ll be in touch soon.</h1>
            </div>
          </div>
        </section>
      )}
    </MarketingLayout>
  )
}

export default Contact;
